import { t as translate } from "../preferences";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from "react";
import { NavLink, useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { useAuth } from "../auth";
import { APP_ROUTES, routeByPath, type AppRoute } from "../routes";
import { useAppEnvironment, useTelegramBackButton } from "../../telegram";
import { historicalEntryBlocked, rememberHistoryEntry } from "../workDraft";
import { consumeLocalBack, useLocalBackCount } from "../navigation";

const BackContext = createContext<(() => void) | null>(null);

export function useMechanicBack() {
  const goBack = useContext(BackContext);
  if (!goBack) throw new Error("Mechanic back navigation requires AppShell");
  return goBack;
}

export function CompactHeader({ children }: { children: ReactNode }) {
  const goBack = useMechanicBack();
  return <header className="m-app-safe-top m-sticky m-top-0 m-z-40 -m-mx-4 m-mb-4 m-border-b m-border-border m-bg-background">
    <div className="m-flex m-min-w-0 m-items-center m-gap-2 m-px-3 m-pb-2 m-pt-2">
      <button type="button" className="m-touch-icon" aria-label={translate("Ortga")} onClick={goBack}><ChevronLeft className="m-size-6" /></button>
      <div className="m-min-w-0 m-flex-1">{children}</div>
    </div>
  </header>;
}

export function AppShell({
  allowedRoutes,
  children,
}: {
  allowedRoutes: readonly AppRoute[];
  children: ReactNode;
}) {
  const { state, access } = useAuth();
  const localBackCount = useLocalBackCount();
  const { isActive, isTelegram } = useAppEnvironment();
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const current = routeByPath(location.pathname);
  const hasBack = location.pathname !== "/mechanic/";

  const goBack = useCallback(() => {
    if (consumeLocalBack()) return;
    if (location.pathname === "/mechanic/work/confirmation") {
      navigate("/mechanic/", { replace: true });
      return;
    }
    const historyState = window.history.state as { idx?: number } | null;
    if ((historyState?.idx ?? 0) > 0) navigate(-1);
    else navigate("/mechanic/", { replace: true });
  }, [location.pathname, navigate]);

  useTelegramBackButton(!access?.recovery && state.status === "authenticated" && !state.user.must_change_password && (hasBack || localBackCount > 0), goBack);

  const isCompletionRoute = location.pathname === "/mechanic/work/confirmation";
  const isHomeRoute = location.pathname === "/mechanic/";
  const compactHeaderRoute = ["/mechanic/history", "/mechanic/work", "/mechanic/clients/new", "/mechanic/statistics", "/mechanic/profile", "/mechanic/profile/edit", "/mechanic/profile/password", "/mechanic/support"].includes(location.pathname) || /^\/mechanic\/clients\/\d+$/.test(location.pathname);
  const warehouseActions: Record<string, string> = {
    "/mechanic/warehouse/purchase": translate("Tovar kirimi"),
    "/mechanic/warehouse/sale": translate("Tovar sotish"),
    "/mechanic/warehouse/purchase-return": translate("Xarid qaytarish"),
    "/mechanic/warehouse/sale-return": translate("Sotuv qaytarish"),
    "/mechanic/warehouse/write-off": translate("Hisobdan chiqarish"),
    "/mechanic/warehouse/jarayonda": translate("Jarayonda"),
  };
  const returnListPath = location.pathname.match(/^\/mechanic\/warehouse\/(purchase-return|sale-return)\/\d+$/)?.[1];
  const warehouseAction = warehouseActions[returnListPath ? `/mechanic/warehouse/${returnListPath}` : location.pathname];
  const warehouseHeader = location.pathname === "/mechanic/warehouse" || warehouseAction !== undefined;
  const blockedHistoricalPop = historicalEntryBlocked(location.key, navigationType) &&
    !isCompletionRoute && !isHomeRoute;

  useEffect(() => {
    if (blockedHistoricalPop) {
      navigate("/mechanic/", { replace: true });
      return;
    }
    // New entries are valid without ever rehabilitating pre-save entries.
    rememberHistoryEntry(location.key);
  }, [blockedHistoricalPop, location.key, navigate]);

  const bottomRoutes = useMemo(
    () => allowedRoutes.filter((route) => route.bottomNav),
    [allowedRoutes],
  );

  return (
    <BackContext.Provider value={goBack}><div
      className="m-app-viewport m-app-safe-inline m-mx-auto m-flex m-w-full m-max-w-lg m-flex-col m-overflow-x-hidden m-bg-background"
      data-app-active={isActive ? "true" : "false"}
      data-environment={isTelegram ? "telegram" : "browser"}
    >
      {warehouseHeader ? (
        <header className="m-app-safe-top m-sticky m-top-0 m-z-40 m-border-b m-border-border m-bg-background">
          <div className="m-flex m-min-w-0 m-items-center m-gap-2 m-px-3 m-pb-2 m-pt-2">
            <button type="button" className="m-touch-icon" aria-label={translate("Ortga")} onClick={goBack}>
              <ChevronLeft className="m-size-6" />
            </button>
            <div className="m-flex m-min-w-0 m-items-center m-gap-1.5 m-text-sm m-font-bold">
              {warehouseAction ? <><span className="m-shrink-0">{translate("Ombor")}</span><ChevronRight className="m-size-4 m-shrink-0 m-text-muted" aria-hidden="true" /><h1 className="m-truncate m-text-sm m-font-bold">{warehouseAction}</h1></>
                : <h1 className="m-text-base m-font-bold">{translate("Ombor")}</h1>}
            </div>
          </div>
        </header>
      ) : !isHomeRoute && !compactHeaderRoute ? (
        <header className="m-app-safe-top m-sticky m-top-0 m-z-40 m-border-b m-border-border m-bg-background/95 m-backdrop-blur">
          <div className="m-flex m-min-h-16 m-min-w-0 m-items-center m-gap-2 m-px-3">
            <button type="button" className="m-touch-icon" aria-label={translate("Ortga")} onClick={goBack}>
              <ChevronLeft className="m-size-6" />
            </button>
            <div className="m-min-w-0 m-flex-1">
              <p className="m-truncate m-text-xs m-font-semibold m-text-muted">{translate("AvtoDaftar Usta")}</p>
              <p className="m-truncate m-text-base m-font-bold m-text-foreground">
                {current?.title ?? translate("AvtoDaftar Usta")}
              </p>
            </div>
          </div>
        </header>
      ) : null}

      <main className={`m-app-content m-min-w-0 m-flex-1 m-px-4 ${compactHeaderRoute ? "m-py-0" : warehouseHeader ? "m-pt-4" : "m-py-5"} ${isHomeRoute ? "m-app-safe-top" : ""}`}>
        {blockedHistoricalPop ? null : children}
      </main>

      <nav data-mechanic-bottom-nav className="m-app-safe-bottom m-app-safe-inline m-fixed m-inset-x-0 m-bottom-0 m-z-40 m-mx-auto m-grid m-w-full m-max-w-lg m-border-t m-border-border m-bg-surface/95 m-backdrop-blur" style={{ gridTemplateColumns: `repeat(${bottomRoutes.length}, minmax(0, 1fr))` }} aria-label={translate("Asosiy bo‘limlar")}>
        {bottomRoutes.map((route) => {
          const Icon = route.icon;
          return (
            <NavLink
              key={route.path}
              to={route.path}
              end={route.path === "/mechanic/"}
              className={({ isActive: active }) =>
                `m-flex m-min-h-16 m-min-w-0 m-flex-col m-items-center m-justify-center m-gap-1 m-px-1 m-text-[10px] m-font-semibold ${active ? "m-text-primary" : "m-text-muted"}`
              }
            >
              <Icon className="m-size-5" aria-hidden="true" />
              <span className="m-max-w-full m-truncate">{route.title}</span>
            </NavLink>
          );
        })}
      </nav>
    </div></BackContext.Provider>
  );
}

export const ALL_ROUTE_PATHS = APP_ROUTES.map((route) => route.path);
